import { PaymentMethod } from '@repo/common';
import { InvoiceGenerator } from '@repo/pdf';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { z } from 'zod';

import {
    useGetOrderByIdQuery,
    useMultiPayForOrderByIdMutation,
} from '@/features/slice/order';
import { ResponseSingleOrderSchema } from '@/features/slice/order/order.schema';
import { useGetStoreSettingsQuery } from '@/features/slice/settings';
import { TAddPaymentForm } from '@/modules/cart/add-payment-form.schema.ts';
import { toastError } from '@/utils';
import { mapOrderToInvoice } from '@/utils/mappers';

export const useOrderDetails = () => {
    const params = useParams();
    const id = params.id as string;
    const { data, error, isLoading, isSuccess, isError } =
        useGetOrderByIdQuery(id);
    const { data: storeSettings } = useGetStoreSettingsQuery('');

    const [
        multiPayOrder,
        {
            isLoading: isMultiPayLoading,
            isError: isMultiPayError,
            error: multiPayError,
        },
    ] = useMultiPayForOrderByIdMutation();

    const [showDiscount, setShowDiscount] = useState(true);

    const handlePrintInvoice = () => {
        if (!data || !data.data) {
            toast.error('Order data is not available for printing.');
            return;
        }

        const invoice = new InvoiceGenerator(
            mapOrderToInvoice(data.data, storeSettings?.data as any),
            { showDiscount, type: 'sale' },
        );
        invoice.print();
    };

    const handleMakePayment = async (payload: TAddPaymentForm) => {
        const totalPayable = payload.payments.reduce(
            (total, payment) => total + (payment.amount ?? 0),
            0,
        );

        // if (totalPayable > data?.)

        const totalPrice = data?.data?.payment?.total_price ?? 0;
        const totalPaid = data?.data?.payment?.total_paid ?? 0;
        const dueAmount = totalPrice - totalPaid;

        try {
            if (totalPayable > dueAmount) {
                if (
                    payload.payments.length === 1 &&
                    payload.payments[0].payment_method === PaymentMethod.CASH
                ) {
                    await multiPayOrder({
                        id: id,
                        payments: [
                            {
                                ...payload.payments[0],
                                amount: dueAmount,
                            },
                        ],
                    }).unwrap();
                    toast.success('Payment successfully created!');
                    return;
                }
            }

            await multiPayOrder({
                id: id,
                payments: payload.payments,
            }).unwrap();
            toast.success('Payment successfully created!');
        } catch (error) {
            toastError(error, 'useOrderDetails');
        }
    };

    return {
        data: data as z.infer<typeof ResponseSingleOrderSchema>,
        error,
        isError,
        isLoading,
        isSuccess,
        handlePrintInvoice,
        handleMakePayment,
        isMultiPayError,
        isMultiPayLoading,
        multiPayError,
        showDiscount,
        setShowDiscount,
    };
};
